// src/pages/Home.tsx

import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Popupchat from "../components/Popupchat";
import Appfooter from "../components/Appfooter";

import Load from "../components/Load";
import Memberslider from "../components/Memberslider";
import Friendsilder from "../components/Friendsilder";
import Postview from "../components/Postview";
import AdvertisementCard from "./AdvertisementCard";

import Friends from "../components/Friends";
import Contacts from "../components/Contacts";
import Group from "../components/Group";
import Events from "../components/Events";
import Profilephoto from "../components/Profilephoto";
import Storyslider from "../components/Storyslider";

import {
  fetchPosts,
  fetchClientPosts,
  fetchUserFeed,
} from "../redux/slices/postSlice";

import { RootState } from "../redux/store";
import { addPostView } from "../redux/slices/postViewSlice";
import { getAdvertisements } from "../redux/slices/advertisementSlice";
import JoinClubCard from "../components/JoinClubCard";

import { getAnnouncementsApi } from "../api/AnnouncementActions";

function useWindowWidth() {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return width;
}

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const getUploadUrl = (
  file?: string | null
) => {
  if (
    !file ||
    !String(file).trim()
  ) {
    return undefined;
  }

  const value = String(file).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  if (value.startsWith("/uploads/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  if (value.startsWith("uploads/")) {
    return encodeURI(`${API_URL}/${value}`);
  }

  if (value.startsWith("/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  return encodeURI(
    `${API_URL}/uploads/${value}`
  );
};


const getShareUrl = (postId: number | string) => {
  return `${API_URL}/share/news-details/${postId}`;
};

const fixContentImageUrls = (html = "") =>
  html
    .replace(/<img[^>]*src=["']\/\/:0["'][^>]*>/gi, "")
    .replace(/<img[^>]*src=["']about:blank["'][^>]*>/gi, "")
    .replace(/src=["']\/uploads\//gi, `src="${API_URL}/uploads/`)
    .replace(/&nbsp;/g, " ");

const stripHtml = (html = "") =>
  fixContentImageUrls(html)
    .replace(/<img[^>]*>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const getPostType = (post: any) => {
  if (post?.youtube_url) return "youtube";
  if (post?.post_video) return "video";
  if (post?.post_image) return "image";
  if (post?.post_content?.includes("<img")) return "content-image";
  return post?.post_type || post?.main_category || "news";
};

type AnnouncementItem = {
  announcement_id: number;
  client_id: number;
  client_name?: string | null;
  logo_image?: string | null;
  logo_image_url?: string | null;
  title?: string | null;
  description?: string | null;
  announcement_type_name?: string | null;
  visibility?: string | null;
  announcement_image_url?: string | null;
  published_on?: string | null;
};

const FeedAnnouncementCard: React.FC<{ announcement: AnnouncementItem }> = ({ announcement }) => (
  <article className="feed-announcement-card">
    <div className="feed-announcement-top">
      <div className="feed-announcement-brand">
        <img
          src={
            getUploadUrl(
              announcement.logo_image_url ||
              announcement.logo_image
            ) || "/assets/images/user.png"
          }
          alt={announcement.client_name || "Club"}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/assets/images/user.png";
          }}
        />
        <div>
          <h1>{announcement.client_name || "Bharat Sangam"}</h1>
          <span>{announcement.announcement_type_name || "Announcement"}</span>
        </div>
      </div>
      <span className="feed-announcement-badge">Announcement</span>
    </div>

    <div className="feed-announcement-content">
      <h2>{announcement.title || "Announcement"}</h2>
      {announcement.description && <p>{announcement.description}</p>}
    </div>

    {announcement.announcement_image_url && (
      <img
        className="feed-announcement-image"
        src={getUploadUrl(announcement.announcement_image_url)}
        alt={announcement.title || "Announcement"}
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    )}

    <div className="feed-announcement-footer">
      <span>
        {announcement.published_on
          ? new Date(announcement.published_on).toLocaleString("en-IN")
          : ""}
      </span>
      <span className="feed-announcement-visibility">
        {announcement.visibility || "public"}
      </span>
    </div>
  </article>
);

const Home: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const width = useWindowWidth();

  const isMobile = width < 768;
  const isTablet = width < 992;
  const isDesktop = width >= 1200;

  const { posts, clientPosts, loading } = useSelector(
    (state: RootState) => state.posts
  );

  const { advertisements } = useSelector(
    (state: RootState) => state.advertisements
  );

  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);

  const selectedClient = JSON.parse(
    localStorage.getItem("selectedClient") || "null"
  );
  const loggedUser = JSON.parse(localStorage.getItem("user") || "null");
  const loggedContact = JSON.parse(localStorage.getItem("contact") || "null");

  const isLoggedIn = Boolean(loggedUser?.user_id);
  const isSlugMode = Boolean(selectedClient?.client_id);

  useEffect(() => {
    if (isSlugMode) {
      dispatch(
        fetchClientPosts({
          client_id: Number(selectedClient.client_id),
          user_id: isLoggedIn ? Number(loggedUser.user_id) : undefined,
        })
      );
    } else if (isLoggedIn) {
      dispatch(fetchUserFeed(Number(loggedUser.user_id)));
    } else {
      dispatch(fetchPosts());
    }

    dispatch(
      getAdvertisements(
        isLoggedIn
          ? Number(loggedUser.user_id)
          : null
      )
    );
  }, [dispatch, isSlugMode, isLoggedIn, selectedClient?.client_id, loggedUser?.user_id]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAnnouncements = async () => {
      try {
        const result = await getAnnouncementsApi(
          {
            client_id:
              isSlugMode && selectedClient?.client_id
                ? Number(selectedClient.client_id)
                : undefined,
            user_id:
              isLoggedIn && loggedUser?.user_id
                ? Number(loggedUser.user_id)
                : undefined,
            page: 1,
            limit: 50,
          },
          controller.signal
        );

        if (
          result?.success === false ||
          result?.status === false
        ) {
          throw new Error(
            result?.message ||
              "Unable to fetch announcements"
          );
        }

        setAnnouncements(
          Array.isArray(result?.data)
            ? result.data
            : []
        );
      } catch (error: any) {
        if (
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "AbortError"
        ) {
          return;
        }

        console.error(
          "Announcement fetch error:",
          error
        );

        setAnnouncements([]);
      }
    };

    fetchAnnouncements();

    return () => {
      controller.abort();
    };
  }, [
    isLoggedIn,
    isSlugMode,
    loggedUser?.user_id,
    selectedClient?.client_id,
  ]);

  const allPosts = isSlugMode ? clientPosts || [] : posts || [];

const handlePostClick = useCallback(
  (e: React.MouseEvent<HTMLDivElement>, post: any, postForDetails: any) => {
    const target = e.target as HTMLElement;

    if (
      target.closest("button") ||
      target.closest("a") ||
      target.closest("iframe") ||
      target.closest("video") ||
      target.closest(".post-actions") ||
      target.closest(".like-button") ||
      target.closest(".comment-button") ||
      target.closest(".share-button")
    ) {
      return;
    }

    dispatch(
      addPostView({
        post_id: post.post_id,
        user_id: loggedUser?.user_id ? Number(loggedUser.user_id) : null,
      }) as any
    );

    navigate(`/news-details/${post.post_id}`, {
      state: {
        post: {
          ...postForDetails,
          share_url: getShareUrl(post.post_id),
        },
      },
    });
  },
  [dispatch, loggedUser?.user_id, navigate]
);

  return (
    <Fragment>
      <Header />
      <Leftnav />
      {isLoggedIn && <Rightchat />}

      <div className="main-content right-chat-active home-modern-page">
        <div className="middle-sidebar-bottom home-middle-bottom">
          <div className="middle-sidebar-left home-middle-left">
            <div className="home-modern-shell">
              <div className="home-feed-body">
                <div className="home-feed-main">
                  {!isLoggedIn ? (
                    <section className="guest-welcome-card">
                      <div className="guest-welcome-content">
                        <span className="guest-welcome-eyebrow">Discover Bharat Sangam</span>
                        <h2>Connect with clubs, communities and meaningful causes.</h2>
                        <p>
                          Explore public updates, events, social initiatives and trusted
                          community organisations across India.
                        </p>

                        <div className="guest-welcome-actions">
                          <button
                            className="guest-primary-action"
                            onClick={() => navigate("/login")}
                          >
                            Login to Continue
                          </button>

                          <button
                            className="guest-secondary-action"
                            onClick={() => navigate("/complete-profile")}
                          >
                            Create Account
                          </button>
                        </div>
                      </div>

                      <div className="guest-welcome-visual" aria-hidden="true">
                        <div className="guest-visual-ring guest-ring-one" />
                        <div className="guest-visual-ring guest-ring-two" />
                        <div className="guest-visual-mark">BS</div>
                      </div>
                    </section>
                  ) : (
                    <div className="home-stories-section">
                      <Storyslider />
                    </div>
                  )}

                  {loading ? (
                    <div className="social-feed-state">Loading posts...</div>
                  ) : allPosts.length > 0 ? (
                    allPosts.map((post: any, index: number) => {
                      const cleanContent = fixContentImageUrls(
                        post.post_content || ""
                      );

                      const galleryImages = (post.gallery_images || []).map(
                        (img: any) => ({
                          ...img,
                          image: getUploadUrl(img.image),
                          url: getUploadUrl(img.image),
                        })
                      );

                      const postForDetails = {
                        ...post,
                        post_content: cleanContent,
                        post_image: getUploadUrl(post.post_image),
                        post_video: getUploadUrl(post.post_video),
                        gallery_images: galleryImages,
                      };

                      // Show one advertisement after every 3 posts.
                      // Ads are displayed in sequence instead of randomly,
                      // so the next available ad is shown each time.
                      const showAdvertisement =
                        (index + 1) % 3 === 0 &&
                        advertisements &&
                        advertisements.length > 0;

                      const advertisementPosition =
                        Math.floor((index + 1) / 3) - 1;

                      const currentAd =
                        advertisements?.length > 0
                          ? advertisements[
                              advertisementPosition %
                                advertisements.length
                            ]
                          : null;

                      return (
                        <React.Fragment key={post.post_id}>
                          <div
                            className="home-post-click"
                            onClick={(e) =>
                              handlePostClick(e, post, postForDetails)
                            }
                          >
                          <Postview
  id={post.post_id}
  clientId={post.client_id}
  user={post.client_name || "News Source"}
  role={post.client_user_role || post.role || ""}
  time={
    post.created_at
      ? new Date(post.created_at).toLocaleString("en-IN")
      : ""
  }
  title={post.title || post.short_description || "Post"}
  shortDescription={post.short_description || ""}
  category={
    post.category_name ||
    post.category ||
    post.main_category ||
    "General"
  }
  mainCategory={post.main_category || "news"}
  des={cleanContent}
  plainText={stripHtml(cleanContent)}
  avater="/assets/images/user.png"
  postimage={getUploadUrl(post.post_image)}
  postvideo={getUploadUrl(post.post_video)}
  logo_image={getUploadUrl(post.logo_image)}
  youtubeUrl={post.youtube_url || ""}
  unique_slug={post.unique_slug || ""}
  galleryImages={galleryImages}
  tags={post.tags || []}
  likeCount={Number(post.like_count || 0)}
  commentCount={Number(post.comment_count || 0)}
  shareCount={Number(post.share_count || 0)}
  viewCount={Number(post.view_count || 0)}
  isLiked={Boolean(post.is_liked)}
  postType={getPostType(post)}
  shareUrl={getShareUrl(post.post_id)}
  onDonate={() => {
    navigate(`/donate/${post.post_id}`, {
      state: {
        donationFrom: isSlugMode ? "slug" : "public",
        post_id: post.post_id,
        client_id: post.client_id,
      },
    });
  }}
/>
                          </div>

                          {(index + 1) % 2 === 0 && announcements.length > 0 && (
                            <div className="home-announcement-wrap">
                              <FeedAnnouncementCard
                                announcement={
                                  announcements[
                                    (Math.floor((index + 1) / 2) - 1) %
                                      announcements.length
                                  ]
                                }
                              />
                            </div>
                          )}

                          {showAdvertisement && currentAd && (
                            <div className="home-ad-wrap">
                              <AdvertisementCard ad={currentAd} />
                            </div>
                          )}

                          {isLoggedIn && index === 2 && (
                            <div className="home-slider-wrap">
                              <Memberslider />
                            </div>
                          )}

                          {isLoggedIn && index === 4 && (
                            <div className="home-slider-wrap">
                              <Friendsilder />
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <section className="home-empty-feed">
                      <div className="home-empty-feed-glow home-empty-feed-glow-one" />
                      <div className="home-empty-feed-glow home-empty-feed-glow-two" />

                      <div className="home-empty-feed-content">
                        <div className="home-empty-feed-icon">
                          <div className="home-empty-feed-icon-ring">
                            <i className="feather-file-text" />
                          </div>
                        </div>

                        <span className="home-empty-feed-eyebrow">
                          Your community feed
                        </span>

                        <h3>Nothing new here yet</h3>

                        <p>
                          Follow clubs and join communities to see posts,
                          announcements, events and updates in your feed.
                        </p>

                        <div className="home-empty-feed-actions">
                          <button type="button" className="home-empty-feed-primary" onClick={() => navigate("/clubs")}>
                            <i className="feather-users" />
                            Explore Clubs
                          </button>

                          <button type="button" className="home-empty-feed-secondary" onClick={() => navigate("/defaultstorie")}>
                            <i className="feather-trending-up" />
                            View Trending
                          </button>
                        </div>

                        <div className="home-empty-feed-tips">
                          <div className="home-empty-feed-tip">
                            <span className="home-empty-feed-tip-icon"><i className="feather-user-plus" /></span>
                        <div>
  <strong>Follow clubs</strong>
  <span>
    Stay updated with posts and news from clubs you follow.
  </span>
</div>
                          </div>

                          <div className="home-empty-feed-tip">
                            <span className="home-empty-feed-tip-icon"><i className="feather-link" /></span>
                            <div>
                              <strong>Join communities</strong>
                              <span>See exclusive updates shared with club members.</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  <Load />
                </div>

                <div className="home-feed-side">
                  <div className="home-sidebar-modern">
                    {isLoggedIn ? (
                      <>
                        <Friends />
                          <JoinClubCard />
                        <Contacts />
                        {/* <Group /> */}
                        <Events />
                        <Profilephoto />
                      </>
                    ) : (
                      <>
                        <div className="home-login-card">
                          <div className="home-login-icon">BS</div>
                          <span className="home-login-kicker">Welcome</span>
                          <h5>Join Bharat Sangam</h5>
                          <p>
                            Follow clubs, connect with members, support causes and receive
                            personalised updates.
                          </p>

                          <button
                            className="home-login-primary"
                            onClick={() => navigate("/login")}
                          >
                            Login Now
                          </button>

                          <button
                            className="home-login-secondary"
                            onClick={() => navigate("/complete-profile")}
                          >
                            Create New Account
                          </button>

                          <div className="home-login-divider">
                            <span>Guest access</span>
                          </div>

                          <ul className="guest-benefit-list">
                            <li>Browse public club updates</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Popupchat />
        <Appfooter />
      </div>

      <style>{`
        .home-modern-page {
          background: #f5f7fb;
          min-height: 100vh;
          overflow-x: hidden;
          padding-top: 18px;
          padding-left: 274px;
          padding-right: ${isLoggedIn && !isTablet ? "330px" : "24px"};
        }

        .home-middle-bottom {
          width: 100%;
        }

        .home-middle-left {
          width: 100%;
          max-width: 100%;
          padding: 0 12px;
          margin: 0;
        }

        .home-modern-shell {
          width: 100%;
          max-width: 1180px;
          margin: 0;
        }

        .home-feed-body {
          display: grid;
          grid-template-columns: minmax(0, 680px) 260px;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 20px;
          width: 100%;
          margin-top: 58px;
        }

        .home-feed-main {
          width: 100%;
          min-width: 0;
          max-width: 680px;
        }

        .home-feed-side {
          width: 260px;
          min-width: 260px;
          max-width: 260px;
          margin-top: 28px;
          flex-shrink: 0;
        }

        .home-sidebar-modern {
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
        }

        .home-stories-section {
          width: 100%;
          margin-bottom: 18px;
          overflow: hidden;
        }

        .home-post-click {
          width: 100%;
          margin-bottom: 18px;
          cursor: pointer;
        }

        .home-ad-wrap,
        .home-slider-wrap,
        .home-announcement-wrap {
          margin-bottom: 18px;
        }

        .feed-announcement-card {
          overflow: hidden;
          border: 1px solid #dbeafe;
          border-radius: 20px;
          background: #ffffff;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
        }

        .feed-announcement-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 16px 18px 12px;
        }

        .feed-announcement-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .feed-announcement-brand img {
          width: 42px;
          height: 42px;
          flex-shrink: 0;
          border: 1px solid #e2e8f0;
          border-radius: 50%;
          object-fit: cover;
          background: #f8fafc;
        }

        .feed-announcement-brand strong {
          display: block;
          color: #0f172a;
          font-size: 14px;
          font-weight: 800;
        }

        .feed-announcement-brand span {
          display: block;
          margin-top: 3px;
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
        }

        .feed-announcement-badge {
          flex-shrink: 0;
          padding: 6px 10px;
          border-radius: 999px;
          background: #e0f2fe;
          color: #0369a1;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .feed-announcement-content { padding: 4px 18px 16px; }
        .feed-announcement-content h3 {
          margin: 0;
          color: #0f172a;
          font-size: 18px;
          font-weight: 800;
          line-height: 1.35;
        }
        .feed-announcement-content p {
          display: -webkit-box;
          overflow: hidden;
          margin: 9px 0 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.65;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }

        .feed-announcement-image {
          display: block;
          width: 100%;
          max-height: 360px;
          object-fit: cover;
          background: #f8fafc;
        }

        .feed-announcement-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 18px;
          border-top: 1px solid #eef2f7;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 600;
        }

        .feed-announcement-visibility {
          padding: 4px 8px;
          border-radius: 999px;
          background: #f1f5f9;
          color: #475569;
          text-transform: capitalize;
        }

        .social-feed-state {
          background: #ffffff;
          border-radius: 18px;
          padding: 36px 20px;
          text-align: center;
          color: #64748b;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 6px 20px rgba(15, 23, 42, 0.05);
        }


        .home-empty-feed {
          position: relative; overflow: hidden; min-height: 330px; margin-bottom: 18px; padding: 38px 28px 26px;
          border: 1px solid #e4ebf5; border-radius: 24px;
          background: linear-gradient(145deg,#ffffff 0%,#fbfdff 55%,#f3f7ff 100%);
          box-shadow: 0 12px 34px rgba(15,23,42,.055); isolation: isolate;
        }
        .home-empty-feed-glow { position:absolute; border-radius:50%; pointer-events:none; z-index:-1; }
        .home-empty-feed-glow-one { width:190px; height:190px; top:-95px; right:-65px; background:rgba(47,111,228,.09); }
        .home-empty-feed-glow-two { width:150px; height:150px; left:-80px; bottom:-80px; background:rgba(14,165,233,.06); }
        .home-empty-feed-content { display:flex; flex-direction:column; align-items:center; max-width:470px; margin:0 auto; text-align:center; }
        .home-empty-feed-icon { display:flex; align-items:center; justify-content:center; width:74px; height:74px; margin-bottom:14px; border-radius:22px; background:linear-gradient(145deg,#eff5ff,#f8fbff); box-shadow:inset 0 0 0 1px rgba(47,111,228,.08),0 10px 24px rgba(47,111,228,.08); }
        .home-empty-feed-icon-ring { display:flex; align-items:center; justify-content:center; width:48px; height:48px; border-radius:15px; background:#fff; color:#2f6fe4; font-size:20px; box-shadow:0 6px 16px rgba(47,111,228,.10); }
        .home-empty-feed-eyebrow { display:inline-flex; align-items:center; min-height:25px; padding:0 10px; border-radius:999px; background:#eef4ff; color:#2f6fe4; font-size:10px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; }
        .home-empty-feed h3 { margin:10px 0 0; color:#334155; font-size:20px; font-weight:750; }
        .home-empty-feed p { max-width:410px; margin:8px 0 0; color:#94a3b8; font-size:12.5px; line-height:1.65; }
        .home-empty-feed-actions { display:flex; justify-content:center; flex-wrap:wrap; gap:9px; margin-top:18px; }
        .home-empty-feed-actions button { display:inline-flex; align-items:center; justify-content:center; gap:7px; min-height:38px; padding:0 15px; border-radius:11px; font-size:11.5px; font-weight:700; cursor:pointer; transition:.15s ease; }
        .home-empty-feed-actions button:hover { transform:translateY(-1px); }
        .home-empty-feed-primary { border:1px solid #2f6fe4; background:#2f6fe4; color:#fff; box-shadow:0 8px 18px rgba(47,111,228,.18); }
        .home-empty-feed-secondary { border:1px solid #dce6f5; background:#fff; color:#64748b; }
        .home-empty-feed-tips { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:9px; width:100%; margin-top:22px; }
        .home-empty-feed-tip { display:flex; align-items:flex-start; gap:9px; padding:11px; border:1px solid #e8eef7; border-radius:13px; background:rgba(255,255,255,.72); text-align:left; }
        .home-empty-feed-tip-icon { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; flex-shrink:0; border-radius:9px; background:#f1f5fb; color:#6b84a8; font-size:13px; }
        .home-empty-feed-tip strong { display:block; color:#475569; font-size:11px; font-weight:700; }
        .home-empty-feed-tip span:not(.home-empty-feed-tip-icon) { display:block; margin-top:2px; color:#9aa7ba; font-size:9.8px; line-height:1.45; }

        .home-login-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 24px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .home-login-card h4 {
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #111827;
        }

        .home-login-card p {
          margin: 0 0 16px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.5;
        }

        .guest-welcome-card {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1fr) 170px;
          gap: 24px;
          overflow: hidden;
          margin-bottom: 18px;
          padding: 30px;
          border: 1px solid #dbeafe;
          border-radius: 24px;
          background:
            radial-gradient(circle at 85% 20%, rgba(14, 165, 233, 0.16), transparent 34%),
            linear-gradient(135deg, #ffffff 0%, #eff6ff 52%, #ecfeff 100%);
          box-shadow: 0 16px 40px rgba(37, 99, 235, 0.08);
        }

        .guest-welcome-content {
          position: relative;
          z-index: 2;
        }

        .guest-welcome-eyebrow,
        .home-login-kicker {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          margin-bottom: 10px;
          padding: 6px 10px;
          border-radius: 999px;
          background: #e0f2fe;
          color: #0369a1;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .guest-welcome-card h1 {
          max-width: 560px;
          margin: 0;
          color: #0f172a;
          font-size: clamp(24px, 3vw, 36px);
          font-weight: 800;
          line-height: 1.18;
          letter-spacing: -0.035em;
        }

        .guest-welcome-card p {
          max-width: 580px;
          margin: 14px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .guest-welcome-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 22px;
        }

        .guest-welcome-actions button,
        .home-login-card button,
        .guest-help-card button {
          border: none;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .guest-welcome-actions button:hover,
        .home-login-card button:hover,
        .guest-help-card button:hover {
          transform: translateY(-1px);
        }

        .guest-primary-action,
        .home-login-primary {
          background: linear-gradient(135deg, #0891b2, #2563eb);
          color: #ffffff;
          box-shadow: 0 10px 22px rgba(37, 99, 235, 0.18);
        }

        .guest-primary-action,
        .guest-secondary-action {
          min-height: 44px;
          padding: 0 18px;
          border-radius: 12px;
          font-size: 13px;
        }

        .guest-secondary-action {
          border: 1px solid #cbd5e1 !important;
          background: rgba(255,255,255,0.86);
          color: #0f172a;
        }

        .guest-welcome-visual {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 150px;
        }

        .guest-visual-mark {
          position: relative;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 82px;
          height: 82px;
          border: 8px solid rgba(255,255,255,0.85);
          border-radius: 50%;
          background: linear-gradient(135deg, #06b6d4, #2563eb);
          color: #ffffff;
          font-size: 22px;
          font-weight: 900;
          box-shadow: 0 18px 36px rgba(37, 99, 235, 0.22);
        }

        .guest-visual-ring {
          position: absolute;
          border: 1px solid rgba(14, 165, 233, 0.28);
          border-radius: 50%;
        }

        .guest-ring-one { width: 130px; height: 130px; }
        .guest-ring-two { width: 175px; height: 175px; }

        .home-login-card {
          position: sticky;
          top: 92px;
          padding: 24px;
          border: 1px solid #e2e8f0;
          border-radius: 22px;
          background: #ffffff;
          text-align: left;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
        }

        .home-login-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 46px;
          height: 46px;
          margin-bottom: 16px;
          border-radius: 15px;
          background: linear-gradient(135deg, #06b6d4, #2563eb);
          color: #ffffff;
          font-size: 14px;
          font-weight: 900;
          box-shadow: 0 10px 20px rgba(37, 99, 235, 0.2);
        }

        .home-login-card h4 {
          margin: 0 0 8px;
          color: #0f172a;
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .home-login-card p {
          margin: 0 0 18px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.6;
        }

        .home-login-card button {
          width: 100%;
          min-height: 44px;
          padding: 0 16px;
          border-radius: 12px;
          font-size: 13px;
        }

        .home-login-secondary {
          margin-top: 10px;
          border: 1px solid #cbd5e1 !important;
          background: #ffffff;
          color: #0f172a;
        }

        .home-login-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 20px 0 14px;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .home-login-divider::before,
        .home-login-divider::after {
          content: "";
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .guest-benefit-list {
          display: grid;
          gap: 10px;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .guest-benefit-list li {
          position: relative;
          padding-left: 24px;
          color: #475569;
          font-size: 12px;
          line-height: 1.45;
        }

        .guest-benefit-list li::before {
          content: "✓";
          position: absolute;
          left: 0;
          top: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 17px;
          height: 17px;
          border-radius: 50%;
          background: #ecfeff;
          color: #0891b2;
          font-size: 10px;
          font-weight: 900;
        }

        .guest-help-card {
          display: grid;
          gap: 7px;
          padding: 18px;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: linear-gradient(145deg, #ffffff, #f8fafc);
        }

        .guest-help-card strong {
          color: #0f172a;
          font-size: 13px;
        }

        .guest-help-card span {
          color: #64748b;
          font-size: 12px;
          line-height: 1.5;
        }

        .guest-help-card button {
          width: fit-content;
          margin-top: 5px;
          padding: 0;
          background: transparent;
          color: #0891b2;
          font-size: 12px;
        }

        @media (max-width: 575px) {
          .home-empty-feed { min-height:0; padding:28px 16px 20px; border-radius:19px; }
          .home-empty-feed h3 { font-size:18px; }
          .home-empty-feed-actions { width:100%; }
          .home-empty-feed-actions button { width:100%; }
          .home-empty-feed-tips { grid-template-columns:1fr; }
        }

        @media (min-width: 1600px) {
          .home-modern-page {
            padding-left: 274px;
            padding-right: ${isLoggedIn ? "340px" : "30px"};
          }

          .home-modern-shell {
            max-width: 1200px;
            margin: 0;
          }

          .home-feed-body {
            grid-template-columns: minmax(0, 700px) 270px;
            gap: 22px;
          }

          .home-feed-main {
            max-width: 700px;
          }

          .home-feed-side {
            width: 270px;
            min-width: 270px;
            max-width: 270px;
          }
        }

        @media (max-width: 1500px) {
          .home-modern-page {
            padding-left: 274px;
            padding-right: ${isLoggedIn && !isTablet ? "320px" : "22px"};
          }

          .home-modern-shell {
            max-width: 1080px;
            margin: 0;
          }

          .home-feed-body {
            grid-template-columns: minmax(0, 650px) 250px;
            gap: 18px;
          }

          .home-feed-main {
            max-width: 650px;
          }

          .home-feed-side {
            width: 250px;
            min-width: 250px;
            max-width: 250px;
          }
        }

        @media (max-width: 1366px) {
          .home-modern-page {
            padding-left: 274px;
            padding-right: ${isLoggedIn && !isTablet ? "300px" : "18px"};
          }

          .home-modern-shell {
            max-width: 980px;
            margin: 0;
          }

          .home-feed-body {
            grid-template-columns: minmax(0, 610px) 240px;
            justify-content: flex-start;
            gap: 16px;
          }

          .home-feed-main {
            max-width: 610px;
          }

          .home-feed-side {
            width: 240px;
            min-width: 240px;
            max-width: 240px;
          }
        }

        @media (max-width: 1200px) {
          .home-modern-page {
            padding-left: 24px;
            padding-right: 18px;
          }

          .home-modern-shell {
            max-width: 100%;
            margin: 0;
          }

          .home-feed-body {
            grid-template-columns: minmax(0, 1fr) 250px;
            justify-content: flex-start;
            gap: 16px;
          }

          .home-feed-main {
            max-width: 100%;
          }

          .home-feed-side {
            width: 250px;
            min-width: 250px;
            max-width: 250px;
          }
        }

        @media (max-width: 991px) {
          .guest-welcome-card {
            grid-template-columns: 1fr;
          }

          .guest-welcome-visual {
            display: none;
          }

          .home-login-card {
            position: static;
          }
          .home-modern-page {
            padding-left: 16px;
            padding-right: 16px;
            padding-top: 12px;
          }

          .home-middle-left {
            padding: 0;
          }

          .home-feed-body {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 14px;
          }

          .home-feed-main,
          .home-feed-side {
            width: 100%;
            max-width: 100%;
            min-width: 0;
          }

          .home-feed-side {
            margin-top: 0;
          }
        }

        @media (max-width: 767px) {
          .guest-welcome-card {
            padding: 22px 18px;
            border-radius: 18px;
          }

          .guest-welcome-card h1 {
            font-size: 25px;
          }

          .guest-welcome-actions {
            flex-direction: column;
          }

          .guest-welcome-actions button {
            width: 100%;
          }
          .home-modern-page {
            padding-left: 10px;
            padding-right: 10px;
            padding-top: 8px;
          }

          .home-feed-body {
            margin-top: 12px;
          }

          .home-feed-main,
          .home-feed-side {
            padding: 0 4px;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default Home;